import { useCallback, useMemo, useEffect, useState, startTransition } from 'react';

const MODAL_QUERY_PARAM = 'modal';
const EMPTY_MODAL = 'modal=EMPTY_MODAL';

/**
 * This implementation currently uses the window.location and window.history APIs, instead
 * of reach/router, because the latter triggers 404s when navigating to the same route and changing
 * only the hash (or the query params).
 *
 * This is a known issue: https://github.com/gatsbyjs/gatsby/discussions/37748
 *
 * Once it is fixed, we will be able to `useLocation` instead of `useState` and `useEffect`.
 * We'll also be able to use the `navigate` function from reach/router instead of `window.location`.
 *
 */
const useModal = () => {
  const [params, setParams] = useState(
    () => new URLSearchParams(typeof window !== 'undefined' ? window.location.hash.slice(1) : '')
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const updateParamsOnHashChange = () => {
      const hash = window.location.hash.slice(1);
      setParams(new URLSearchParams(hash === EMPTY_MODAL ? '' : hash));
    };

    window.addEventListener('popstate', updateParamsOnHashChange);

    return () => {
      window.removeEventListener('popstate', updateParamsOnHashChange);
    };
  }, []);

  const open = useCallback(
    (modalID: string) => {
      startTransition(() => {
        if (params.has(MODAL_QUERY_PARAM, modalID)) {
          return;
        }

        params.append(MODAL_QUERY_PARAM, modalID);
        window.location.hash = `#${params.toString()}`;
      });
    },
    [params]
  );

  const close = useCallback(
    (modalID: string) => {
      startTransition(() => {
        params.delete(MODAL_QUERY_PARAM, modalID);
        const newHash = params.toString();
        window.location.hash = `#${newHash || EMPTY_MODAL}`;

        if (newHash.length === 0) {
          window.history.replaceState({}, '', window.location.href.split('#')[0]);
        }
      });
    },
    [params]
  );

  const isOpen = useCallback(
    (modalID?: string) => {
      return params.has(MODAL_QUERY_PARAM, modalID);
    },
    [params]
  );

  return useMemo(
    () => ({
      open,
      close,
      isOpen,
    }),
    [open, close, isOpen]
  );
};

export default useModal;
